import { ClockCircleOutlined } from '@ant-design/icons';
import { RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import styled from 'styled-components';

import { Flex, Grid, VFlex } from '@/components/base';
import ToggleRadio from '@/components/Common/ToggleRadio';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import { ScheduleSelect } from '@/components/ScheduleSelect/ScheduleSelect';
import Typo from '@/components/Typography/Typo';
import service from '@/services';
import { queryClient } from '@/shared/lib/query-client';
import useDashboard2Store from '@/stores/Dashboard2Store';
import { useToast } from '@/stores/pure/ToastStore';
import { modalStyle } from '@/styles/style';
import { SCHEDULE_PERIOD, WEEKDAY } from '@/types/commonConst';

const ChartAnalysisSettingModal = () => {
  const addToast = useToast();
  const dashboard = useDashboard2Store((state) => state.currDashboard);
  if (!dashboard) throw new Error('Dashboard not found');

  const { closeModal } = useModalContext();
  const [enable, setEnable] = useState(dashboard.analysis_schedule_enable);
  const [payload, setPayload] = useState<Schedule>({
    period: dashboard.analysis_schedule?.period || SCHEDULE_PERIOD.WEEKLY,
    hour: dashboard.analysis_schedule?.hour || 1,
    date: dashboard.analysis_schedule?.date || 1,
    weekday: dashboard.analysis_schedule?.weekday || WEEKDAY.MONDAY,
  });

  const buildPayload = (payload: Schedule) => {
    if (payload.period === SCHEDULE_PERIOD.WEEKLY) {
      return {
        ...payload,
        date: undefined,
      };
    }
    if (payload.period === SCHEDULE_PERIOD.MONTHLY) {
      return { ...payload, weekday: undefined };
    }
    if (payload.period === SCHEDULE_PERIOD.DAILY) {
      return {
        ...payload,
        date: undefined,
        weekday: undefined,
      };
    }
    throw new Error('INVALID PERIOD');
  };

  const onConfirm = async (payload: Schedule) => {
    const cleanedPayload = buildPayload(payload);
    await service.analysis.updateAnalysisSetting(
      dashboard.alias,
      cleanedPayload,
      enable,
    );
    closeModal();
    queryClient.refetchQueries({ queryKey: ['dashboard2', dashboard.alias] });
    addToast('Smart brief setting updated', 'success');
  };

  return (
    <Style>
      <Modal.Header>
        <Modal.Title>Smart brief setting</Modal.Title>
        <Modal.Desc>
          Change the settings of AI boxes associated with this dashboard.
        </Modal.Desc>
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-3">
        <Grid columns="136px 1fr" gap="20px" style={{ height: 40 }}>
          <MenuLabel label="Auto update" icon={<RefreshCw size={14} />} />
          <Flex a="center">
            <ToggleRadio
              status={enable}
              onChange={(status) => setEnable(status)}
            />
          </Flex>
        </Grid>
        <Grid columns="136px 1fr" gap="12px" style={{ height: 40 }}>
          <MenuLabel
            label="When"
            icon={<ClockCircleOutlined style={{ fontSize: 14 }} />}
          />
          <ScheduleSelect schedule={payload} setSchedule={setPayload} />
        </Grid>
      </Modal.Body>
      <Modal.Footer
        cancel={{
          onClick: closeModal,
        }}
        confirm={{
          children: 'Save',
          onClick: () => onConfirm(payload),
        }}
      />
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 600px;
  ${modalStyle};
`;

const MenuLabel = (props: { label: string; icon: ReactNode }) => {
  return (
    <Flex g={6} a="center">
      <Flex style={{ fontSize: 16 }} className="text-gray-700">
        {props.icon}
      </Flex>
      <VFlex>
        <Typo type="label2" weight="m" className="text-gray-700">
          {props.label}
        </Typo>
      </VFlex>
    </Flex>
  );
};

export default ChartAnalysisSettingModal;
